import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import * as THREE from 'three';

// A single agave — cluster of triangular leaves (static; wobble is handled at parent level)
const Agave = ({ position = [0, 0, 0], scale = 1, color = '#7A8A6E' }) => {
    const leaves = useMemo(() => {
        const arr = [];
        const count = 9;
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            const tilt = 0.9 + (i % 2) * 0.1;
            arr.push({ angle, tilt });
        }
        return arr;
    }, []);
    return (
        <group position={position} scale={scale}>
            {leaves.map((l, i) => (
                <mesh
                    key={i}
                    position={[Math.cos(l.angle) * 0.05, 0.15, Math.sin(l.angle) * 0.05]}
                    rotation={[Math.PI / 2 - l.tilt, l.angle, 0]}
                >
                    <coneGeometry args={[0.09, 0.55, 4]} />
                    <meshLambertMaterial color={color} />
                </mesh>
            ))}
        </group>
    );
};

// Decorative grasses - vertical thin cones (static)
const Grass = ({ position = [0, 0, 0], count = 12, radius = 0.5 }) => {
    const blades = useMemo(() => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            const a = Math.random() * Math.PI * 2;
            const r = Math.random() * radius;
            arr.push({
                x: Math.cos(a) * r,
                z: Math.sin(a) * r,
                h: 0.35 + Math.random() * 0.4,
                tilt: (Math.random() - 0.5) * 0.4,
            });
        }
        return arr;
    }, [count, radius]);
    return (
        <group position={position}>
            {blades.map((b, i) => (
                <mesh key={i} position={[b.x, b.h / 2, b.z]} rotation={[b.tilt, 0, b.tilt * 0.6]}>
                    <coneGeometry args={[0.03, b.h, 4]} />
                    <meshLambertMaterial color="#c9a86a" />
                </mesh>
            ))}
        </group>
    );
};

// Small rocks
const Rock = ({ position, scale = 1, color = '#4a3f34' }) => (
    <mesh position={position} scale={scale}>
        <dodecahedronGeometry args={[0.25, 0]} />
        <meshLambertMaterial color={color} flatShading />
    </mesh>
);

export const Plants = forwardRef((props, ref) => {
    const groupRef = useRef();
    useImperativeHandle(ref, () => ({
        setProgress: (v) => {
            if (!groupRef.current) return;
            const p = THREE.MathUtils.clamp(v / 0.55, 0, 1);
            const s = THREE.MathUtils.lerp(0.75, 1.05, p);
            groupRef.current.children.forEach((c) => {
                c.scale.setScalar(s * (c.userData.baseScale || 1));
            });
        },
    }));
    return (
        <group ref={groupRef} name="plants">
            {/* Agaves flanking porch */}
            <group userData={{ baseScale: 1.1 }}><Agave position={[-2.6, 0, 3.2]} scale={1.1} /></group>
            <group userData={{ baseScale: 1.0 }}><Agave position={[2.4, 0, 3.4]} scale={0.9} /></group>
            <group userData={{ baseScale: 0.9 }}><Agave position={[-6.2, 0, 3.6]} scale={0.9} color="#6b7a5e" /></group>
            <group userData={{ baseScale: 1.05 }}><Agave position={[6.6, 0, 3.8]} scale={1.05} /></group>
            <group userData={{ baseScale: 0.8 }}><Agave position={[-8.5, 0, 2.2]} scale={0.75} color="#8b9b7e" /></group>

            {/* Grasses */}
            <group userData={{ baseScale: 1 }}><Grass position={[-3.7, 0, 3.5]} count={14} radius={0.7} /></group>
            <group userData={{ baseScale: 1 }}><Grass position={[3.5, 0, 4.0]} count={12} radius={0.6} /></group>
            <group userData={{ baseScale: 1 }}><Grass position={[7.5, 0, 5.0]} count={16} radius={0.9} /></group>
            <group userData={{ baseScale: 1 }}><Grass position={[-7.5, 0, 5.4]} count={16} radius={0.9} /></group>

            {/* Rocks */}
            <Rock position={[-1.8, 0.1, 3.9]} scale={1.2} />
            <Rock position={[1.9, 0.1, 4.1]} scale={0.9} />
            <Rock position={[-5.4, 0.1, 4.4]} scale={1.4} color="#3a3128" />
            <Rock position={[5.8, 0.1, 4.7]} scale={1.1} color="#3a3128" />
        </group>
    );
});
Plants.displayName = 'Plants';
